import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEnhancedAuth } from '@/contexts/AuthContext';
import { loadPendingSocialSignup } from '@/utils/pendingSocialSignup';
import { logger } from '@/utils/logger';

/**
 * Never leave an authenticated user stranded on an auth page (the trial-signup
 * page, and any other page that hosts a sign-in surface).
 *
 * Why this exists — the stranding bug: in an in-app browser the
 * `pendingSocialSignup` sessionStorage bridge can be LOST across the
 * `signInWithRedirect` round-trip. When it's lost, `useSocialRedirectCompletion`
 * completes the redirect and establishes the session (so `currentUser` is set),
 * but `TrialSignupFlow`'s redirect effect early-returns (`if (!pending) return`)
 * and never navigates. The user ends up authenticated-but-not-routed, stuck on
 * the signup page. This guard closes that gap by routing off the page the moment
 * `currentUser` is set — independent of the bridge.
 *
 * Decision (existing vs. new):
 *  - `hasProfile` (a `getUserProfile` doc exists) → returning user → `/dashboard`.
 *  - authenticated but no profile yet → new user → `/trial-setup`, the same
 *    place `TrialSignupFlow`'s happy path sends new users (AccountSetupPage,
 *    which fails forward into the app / soft onboarding). This also covers the
 *    bridge-lost NEW-user edge, whose trial context (exam/consent) is gone: we
 *    don't try to reconstruct it — we route them in as an authenticated user and
 *    let onboarding / the server-side trial take over.
 *
 * Ceding & safety:
 *  - While a signup redirect is mid-flight (bridge present), cede — TrialSignupFlow
 *    owns completion + `createTrialUser` + its own navigate. Firing here would
 *    race it.
 *  - Wait while user-data is still loading, so `hasProfile` is trustworthy.
 *  - A ceiling guarantees we still route (fail forward) even if user-data never
 *    settles — a signed-in user is never trapped.
 *
 * `skip`: opt out when the page legitimately hosts a signed-in visitor who has
 * NOT finished what they came for. /onboarding uses it for a Pass Plan mint —
 * verified Auth, thin profile, but no trial yet — who would otherwise be swept
 * to the dashboard mid-walk and never get the trial provisioned at all.
 */
/** Returns true when this visitor is being routed off the page, so the page can skip its own view events. */
export function useRedirectAuthenticatedUser(options?: { skip?: boolean }): boolean {
  const { currentUser, hasProfile, isLoading } = useEnhancedAuth();
  const navigate = useNavigate();
  const skip = options?.skip ?? false;

  useEffect(() => {
    if (skip) return; // caller owns this user's routing (see the `skip` note above)
    if (!currentUser) return; // not authenticated → belongs on this page
    if (loadPendingSocialSignup()) return; // TrialSignupFlow is finishing the signup
    if (isLoading) return; // wait for the profile to resolve before deciding

    const target = hasProfile ? '/dashboard' : '/trial-setup';
    logger.info('useRedirectAuthenticatedUser', 'Routing authenticated user off auth page', {
      uid: currentUser.uid,
      hasProfile,
      target,
    });
    navigate(target, { replace: true });
  }, [currentUser, hasProfile, isLoading, navigate, skip]);

  // Safety ceiling: never strand a signed-in user, even if user-data never
  // settles (e.g. createTrialUser is slow / the doc never lands). AccountSetupPage
  // then fails forward into the app.
  useEffect(() => {
    if (skip) return;
    if (!currentUser) return;
    if (loadPendingSocialSignup()) return;
    const timer = setTimeout(() => {
      logger.warn('useRedirectAuthenticatedUser', 'User-data ceiling reached — failing forward', {
        uid: currentUser.uid,
      });
      navigate('/trial-setup', { replace: true });
    }, 8000);
    return () => clearTimeout(timer);
  }, [currentUser, navigate, skip]);

  return !skip && Boolean(currentUser) && !loadPendingSocialSignup();
}
